<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte';
  import { pan } from 'svelte-hammer';
  import Hammer from 'hammerjs';
  import pinch from '../utils/pinch';
  import { clamp } from '../utils/math';

  let container: HTMLElement;

  /** WheelEvent plus the non-standard delta properties the handler below falls back on. */
  type LegacyWheelEvent = WheelEvent & {
    delta?: number;
    wheelDelta?: number;
    originalEvent?: { detail: number };
  };

  let pos = { top: 0, left: 0, x: 0, y: 0 };

  interface Props {
    clazz?: string;
    zoomable?: boolean;
    /**
     * Zoom level to start at. Clamped to the same range as interactive zooming, and
     * applied once on mount -- changing it later does not re-zoom, by design, so it
     * cannot fight the user mid-gesture. The view is centred on the content afterwards,
     * so a value above 1 starts zoomed in on the middle.
     */
    initialScale?: number;
    /**
     * Called when a zoom gesture starts and once it has settled.
     *
     * Canvas-based children need this: reallocating a canvas' backing buffer clears it,
     * so anything that resizes itself to fit (vis-network does, via a ResizeObserver)
     * will blank on every frame of a zoom. Consumers can use these to hold their size
     * for the duration of the gesture and re-fit once at the end.
     */
    onZoomStart?: () => void;
    onZoomEnd?: () => void;
    children?: import('svelte').Snippet;
  }

  let {
    clazz = '',
    zoomable = false,
    initialScale = 1,
    onZoomStart = () => {},
    onZoomEnd = () => {},
    children,
  }: Props = $props();

  /** How long after the last zoom step the gesture counts as finished. */
  const ZOOM_SETTLE_MS = 150;

  let zooming = false;
  let zoomSettleTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Debounced rather than tied to pinchstart/pinchend, so wheel zooming -- which has
   * no start or end event -- gets the same treatment.
   */
  function markZooming() {
    if (!zooming) {
      zooming = true;
      onZoomStart();
    }

    clearTimeout(zoomSettleTimer);
    zoomSettleTimer = setTimeout(() => {
      zooming = false;
      onZoomEnd();
    }, ZOOM_SETTLE_MS);
  }

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  /** Frame (i.e. this component's own box) size, kept current by a ResizeObserver. */
  let frameWidth = $state(0);
  let frameHeight = $state(0);

  let resizeObserver: ResizeObserver | undefined;

  /**
   * The point of the content sitting at the middle of the frame, as a 0..1 fraction of
   * the content's own size. Captured before a resize so the same place can be put back
   * under the middle afterwards.
   */
  function frameCentreFraction() {
    const size = baseSize * scale;
    if (!size) {
      return { x: 0.5, y: 0.5 };
    }
    return {
      x: (container.scrollLeft + frameWidth / 2) / size,
      y: (container.scrollTop + frameHeight / 2) / size,
    };
  }

  /** Puts `fraction` back under the middle of the frame. The browser clamps on any axis
   * with no room to scroll, which is the wanted behaviour there. */
  function scrollToFraction(fraction: { x: number; y: number }) {
    const size = Math.max(frameWidth, frameHeight) * scale;
    container.scrollLeft = fraction.x * size - frameWidth / 2;
    container.scrollTop = fraction.y * size - frameHeight / 2;
  }

  async function measureFrame() {
    if (!container) {
      return;
    }

    const width = container.clientWidth;
    const height = container.clientHeight;

    // An ancestor can take the element out of flow -- a screen that unloads, a panel
    // that closes -- and that reports 0x0. Holding the last real measurement stops the
    // content collapsing to nothing and losing the view for when it comes back.
    if (!width || !height) {
      return;
    }
    if (width === frameWidth && height === frameHeight) {
      return;
    }

    // The content is a multiple of the frame, so resizing the frame rescales it around
    // a scroll offset that stays where it was -- which slides the view somewhere else
    // entirely, and on an axis that loses its scroll range the browser clamps the old
    // offset away for good. Re-anchor on the middle so the resize keeps your place.
    const centre = frameCentreFraction();

    frameWidth = width;
    frameHeight = height;

    // Let the size derived from the new frame land first: until it does the browser
    // clamps the scroll against the old extents.
    await tick();
    scrollToFraction(centre);
  }

  function centreOnContent() {
    if (!container) {
      return;
    }
    container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
    container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
  }

  /**
   * Hammer's pan recognizer defaults to DIRECTION_HORIZONTAL, which means a drag that
   * starts straight up or down is never recognised at all. The map needs both axes.
   */
  const panOptions = { direction: Hammer.DIRECTION_ALL };

  function onWheel(event: LegacyWheelEvent) {
    if (zoomable) {
      // Legacy wheel-delta fallbacks: `wheelDelta` is non-standard (old WebKit) and
      // `originalEvent` only exists when the event arrives via a wrapper library.
      // `?.` so a browser with neither no longer throws here.
      const delta = event.delta || event.wheelDelta || event.originalEvent?.detail || 0;
      throttleZoomBy(delta, event.pageX, event.pageY);
      event.preventDefault();
    }
  }

  let dragging = false;
  let pinching = false;
  let scale = $state(1);

  function panStart(event: CustomEvent<HammerInput>) {
    // A pinch always lands a first finger before its second, which starts a pan. Once
    // the pinch takes over we drop the drag rather than scrolling and zooming at once.
    if (pinching) {
      return;
    }
    dragging = true;

    pos = {
      // The current scroll
      left: container.scrollLeft,
      top: container.scrollTop,
      // Get the current mouse position
      x: event.detail.center.x,
      y: event.detail.center.y,
    };
  }

  function panEnd() {
    dragging = false;
  }
  function onPan(event: CustomEvent<HammerInput>) {
    if (dragging && !pinching) {
      // How far the mouse has been moved
      const dx = event.detail.center.x - pos.x;
      const dy = event.detail.center.y - pos.y;

      // Scroll the element
      container.scrollTop = pos.top - dy;
      container.scrollLeft = pos.left - dx;
    }
  }

  /** The zoom level when the current pinch began, so scaling can track the fingers 1:1. */
  let scaleAtPinchStart = 1;

  function pinchStart() {
    if (!zoomable) {
      return;
    }
    pinching = true;
    // The second finger cancels the pan that the first one started.
    dragging = false;
    scaleAtPinchStart = scale;
  }

  function onPinch(event: CustomEvent<HammerInput>) {
    if (pinching) {
      // event.detail.scale is cumulative from the start of the gesture, so applying it
      // to the scale we started at keeps the zoom proportional to the fingers. The old
      // code differentiated it and multiplied by 50, which saturated the +/-1 cap on
      // essentially every frame and made every pinch a maximum-size step.
      applyScale(
        scaleAtPinchStart * event.detail.scale,
        event.detail.center.x,
        event.detail.center.y,
      );
    }
  }

  function pinchEnd() {
    // Without this `pinching` stayed true for the lifetime of the component.
    pinching = false;
  }

  const throttleZoomBy = throttle(zoomBy, 50);

  /** Wheel zooming: a capped relative step, as before. */
  function zoomBy(delta: number, x: number, y: number) {
    // cap the delta to [-1,1] for cross browser consistency
    const capped = Math.max(-1, Math.min(1, delta));
    applyScale(scale * (1 + capped * 0.25), x, y);
  }

  /**
   * Zooms to `nextScale`, keeping whatever content sits under (x, y) in the same place
   * on screen. Works from the content coordinate under the pointer rather than a
   * percentage of the element's current size, which is what previously made the anchor
   * drift once you were already zoomed in.
   */
  async function applyScale(nextScale: number, x: number, y: number) {
    const rect = container.getBoundingClientRect();
    const pointerX = x - rect.left;
    const pointerY = y - rect.top;

    const prevScale = scale;
    const newScale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    if (newScale === prevScale) {
      return;
    }

    // Where the fingers are in unscaled content units.
    const contentX = (container.scrollLeft + pointerX) / prevScale;
    const contentY = (container.scrollTop + pointerY) / prevScale;

    markZooming();
    scale = newScale;

    // The zoom container is sized reactively off `scale`, so wait for that to land
    // before correcting the scroll position against the new size.
    await tick();

    container.scrollLeft = contentX * newScale - pointerX;
    container.scrollTop = contentY * newScale - pointerY;
  }

  onMount(async () => {
    // Attached by hand because a wheel listener on window is passive by default,
    // and a passive one cannot preventDefault the page scroll away while zooming.
    // Legacy mode's `on:wheel|nonpassive` has no runes equivalent.
    window.addEventListener('wheel', onWheel, { passive: false });

    measureFrame();

    // The frame can change without the window doing so -- the player column reflows at
    // breakpoints -- so watch the element itself rather than listening for resize.
    if (typeof ResizeObserver !== 'undefined' && container) {
      resizeObserver = new ResizeObserver(measureFrame);
      resizeObserver.observe(container);
    }

    if (zoomable) {
      scale = clamp(initialScale, MIN_SCALE, MAX_SCALE);
    }

    // Let the size derived from `scale` land before centring against it. Centring used
    // to target `element`, which has no overflow and so is not a scroll container --
    // the expression always evaluated to 0.
    await tick();
    centreOnContent();
  });

  onDestroy(() => {
    // Don't let a pending settle fire onZoomEnd after the component has gone.
    clearTimeout(zoomSettleTimer);
    resizeObserver?.disconnect();
    window.removeEventListener('wheel', onWheel);
  });

  function throttle<A extends Array<unknown>>(cb: (...args: A) => void, delay: number) {
    let wait = false;

    return (...args: A) => {
      if (wait) {
        return;
      }

      cb(...args);
      wait = true;
      setTimeout(() => {
        wait = false;
      }, delay);
    };
  }
  /**
   * The content is a square, and at MIN_SCALE its side covers the LONGEST edge of the
   * frame -- so it always fills the frame on both axes, cropping on the short axis the
   * way `background-size: cover` does.
   *
   * This used to be `min(100vh, 100vw)`, which covers the SHORT edge: on a widescreen
   * frame that left the sides empty. Measuring the frame rather than the viewport also
   * means it stays correct when the scrollable is only part of the page, which it is
   * here -- the map sits beside the player column.
   */
  let baseSize = $derived(Math.max(frameWidth, frameHeight));
  let contentSize = $derived(baseSize * scale);
</script>

<!-- on:mousedown={onMouseDown} -->
<!--
  Every pinch event is bound here, on one element, so a single Hammer manager owns the
  whole gesture. pinchstart used to live here while pinchin/pinchout sat on
  <svelte:window>, i.e. two independent recognisers, which made the cumulative
  `scale` that pinchStart and onPinch each read come from different sessions.
-->
<section
  use:pan={panOptions}
  onpanstart={panStart}
  use:pinch
  onpinchstart={pinchStart}
  onpinchin={onPinch}
  onpinchout={onPinch}
  onpinchend={pinchEnd}
  onpinchcancel={pinchEnd}
  class={clazz}
  bind:this={container}
>
  {#if zoomable}
    <div style="width: {contentSize}px; height: {contentSize}px" class="zoomContainer">
      {@render children?.()}
    </div>
  {:else}
    {@render children?.()}
  {/if}
</section>

<!-- on:mouseup={onMouseUp} -->
<!-- on:mousemove={onMouseMove} -->
<!--
  pancancel matters: when a second finger lands, Hammer cancels the in-flight pan
  rather than ending it, so panend never fired and `dragging` stayed true after a pinch.
-->
<svelte:window use:pan={panOptions} onpanend={panEnd} onpancancel={panEnd} onpan={onPan} />

<style lang="less">
  section {
    overflow-x: hidden;
    overflow-y: hidden;

    width: 100%;
    height: 100%;
    position: relative;
  }

  .zoomContainer {
    width: 100%;
    height: 100%;
    // Guarantees the frame is covered even before the first measurement lands, so
    // there is never a flash of a gap on mount.
    min-width: 100%;
    min-height: 100%;
    transition: transform 0.1s linear;
    position: absolute;
    transform-origin: 0 0;
    will-change: transform, width, height;
  }
</style>
